import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import Select from "react-select";

import { adminGet, adminMutation, errorMessage } from "./api";

const initialDate = value => value ? new Date(value).toISOString().slice(0, 10) : "";
export const TERMINATE_CONFIRMATION = "DELETE";

function ModalError({ message }) {
  return message ? <Alert variant="danger">{message}</Alert> : null;
}

export function EditAllocationModal({ allocation, catalog, show, onHide, onComplete }) {
  const [form, setForm] = useState({ planId: "", expirationDate: "", reason: "" });
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState("");
  const plans = useMemo(() => (catalog?.plans || []).filter(plan =>
    plan.category === allocation.product?.category || plan.productKey === allocation.product?.productKey
  ), [allocation.product?.category, allocation.product?.productKey, catalog]);

  useEffect(() => {
    if (show) {
      setForm({ planId: allocation.product?.id || "", expirationDate: initialDate(allocation.expirationDate), reason: "" });
      setFailure("");
    }
  }, [allocation, show]);

  const submit = async event => {
    event.preventDefault();
    setSaving(true); setFailure("");
    try {
      await adminMutation("patch", `/allocations/${allocation.id}`, {
        planId: form.planId,
        expirationDate: form.expirationDate || null,
        billingTreatment: "complimentary",
        reason: form.reason,
        version: allocation.version,
      });
      onHide(); onComplete();
    } catch (error) { setFailure(errorMessage(error)); }
    finally { setSaving(false); }
  };

  return <Modal show={show} onHide={onHide} centered><Form onSubmit={submit}>
    <Modal.Header closeButton><Modal.Title>Edit allocation</Modal.Title></Modal.Header>
    <Modal.Body>
      <ModalError message={failure} />
      <Alert variant="info">
        This changes the complimentary allocation's plan or expiration date. Changing its plan republishes its resolved settings or reprovisions the product. Paid plan changes must use checkout so billing can be prorated.
      </Alert>
      <Form.Group className="mb-3"><Form.Label>Product plan</Form.Label><Form.Select required value={form.planId} onChange={event => setForm(current => ({ ...current, planId: event.target.value }))}>
        {plans.map(plan => <option key={plan.id} value={plan.id}>{plan.productName} — {plan.planName}</option>)}
      </Form.Select></Form.Group>
      <Form.Group className="mb-3"><Form.Label>Expiration date</Form.Label><Form.Control type="date" value={form.expirationDate} onChange={event => setForm(current => ({ ...current, expirationDate: event.target.value }))} /><Form.Text>Leave blank for no expiration.</Form.Text></Form.Group>
      <Form.Group><Form.Label>Reason</Form.Label><Form.Control as="textarea" rows={3} required maxLength={1000} value={form.reason} onChange={event => setForm(current => ({ ...current, reason: event.target.value }))} /></Form.Group>
    </Modal.Body>
    <Modal.Footer><Button variant="outline-secondary" onClick={onHide}>Cancel</Button><Button type="submit" disabled={saving}>{saving ? "Saving…" : "Save allocation"}</Button></Modal.Footer>
  </Form></Modal>;
}

export function AssignUserModal({ allocation, licenseeId, show, onHide, onComplete }) {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [role, setRole] = useState("User");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState("");

  useEffect(() => {
    if (!show) return undefined;
    let active = true;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const result = await adminGet("/users", { licenseeId, search: query, size: 20 });
        if (active) setOptions((result.items || []).map(user => ({ value: user.id, label: user.name ? `${user.name} — ${user.email}` : user.email || user.id })));
      } catch (error) { if (active) setFailure(errorMessage(error)); }
      finally { if (active) setLoading(false); }
    }, 250);
    return () => { active = false; clearTimeout(timer); };
  }, [licenseeId, query, show]);

  useEffect(() => {
    if (show) { setQuery(""); setSelected(null); setRole("User"); setReason(""); setFailure(""); }
  }, [show]);

  const submit = async event => {
    event.preventDefault();
    if (!selected) { setFailure("Choose a user."); return; }
    setSaving(true); setFailure("");
    try {
      await adminMutation("post", `/allocations/${allocation.id}/users`, { personId: selected.value, role, reason });
      onHide(); onComplete();
    } catch (error) { setFailure(errorMessage(error)); }
    finally { setSaving(false); }
  };

  return <Modal show={show} onHide={onHide} centered><Form onSubmit={submit}>
    <Modal.Header closeButton><Modal.Title>Assign user to {allocation.product?.productName}</Modal.Title></Modal.Header>
    <Modal.Body>
      <ModalError message={failure} />
      <Form.Group className="mb-3"><Form.Label>User</Form.Label><Select aria-label="User" classNamePrefix="react-select" value={selected} options={options} isLoading={loading} onInputChange={(value, meta) => { if (meta.action === "input-change") setQuery(value); }} onChange={setSelected} placeholder="Search by name or email…" noOptionsMessage={() => query ? "No matching users" : "Start typing a name or email"} /></Form.Group>
      <Form.Group className="mb-3"><Form.Label>Role</Form.Label><Form.Select value={role} onChange={event => setRole(event.target.value)}><option value="User">User</option><option value="ProductManager">Product manager</option></Form.Select></Form.Group>
      <Form.Group><Form.Label>Reason</Form.Label><Form.Control as="textarea" rows={3} required maxLength={1000} value={reason} onChange={event => setReason(event.target.value)} /></Form.Group>
    </Modal.Body>
    <Modal.Footer><Button variant="outline-secondary" onClick={onHide}>Cancel</Button><Button type="submit" disabled={saving || !selected}>{saving ? "Assigning…" : "Assign user"}</Button></Modal.Footer>
  </Form></Modal>;
}

export function TerminateAllocationModal({ allocation, show, onHide, onComplete }) {
  const [confirmation, setConfirmation] = useState("");
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState("");
  useEffect(() => { if (show) { setConfirmation(""); setFailure(""); } }, [show]);
  const submit = async event => {
    event.preventDefault(); setSaving(true); setFailure("");
    try {
      await adminMutation("post", `/allocations/${allocation.id}/terminate`, {
        confirmation,
        reason: "Allocation terminated by a Feradel administrator.",
        version: allocation.version,
        acknowledgeBillingImpact: true,
        acknowledgeProvisioningImpact: true,
      });
      onHide(); onComplete();
    } catch (error) { setFailure(errorMessage(error)); }
    finally { setSaving(false); }
  };
  return <Modal show={show} onHide={onHide} centered><Form onSubmit={submit}>
    <Modal.Header closeButton><Modal.Title>Terminate allocation</Modal.Title></Modal.Header>
    <Modal.Body><ModalError message={failure} /><Alert variant="danger">This ends access to {allocation.product?.productName}, stops its active allocation, and starts product deprovisioning. This action cannot be undone from this dialog.</Alert><Form.Group><Form.Label>Type {TERMINATE_CONFIRMATION} to confirm</Form.Label><Form.Control autoFocus autoComplete="off" value={confirmation} onChange={event => setConfirmation(event.target.value)} /></Form.Group></Modal.Body>
    <Modal.Footer><Button variant="outline-secondary" onClick={onHide}>Cancel</Button><Button variant="danger" type="submit" disabled={saving || confirmation !== TERMINATE_CONFIRMATION}>{saving ? "Terminating…" : "Terminate allocation"}</Button></Modal.Footer>
  </Form></Modal>;
}

export function IssueCreditModal({ licensee, show, onHide, onComplete }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState("");
  useEffect(() => { if (show) { setAmount(""); setReason(""); setFailure(""); } }, [show]);
  const submit = async event => {
    event.preventDefault(); setSaving(true); setFailure("");
    try {
      await adminMutation("post", "/credits", { licenseeId: licensee.id, amount, currency: "USD", reason });
      onHide(); onComplete();
    } catch (error) { setFailure(errorMessage(error)); }
    finally { setSaving(false); }
  };
  return <Modal show={show} onHide={onHide} centered><Form onSubmit={submit}>
    <Modal.Header closeButton><Modal.Title>Issue account credit</Modal.Title></Modal.Header>
    <Modal.Body><ModalError message={failure} /><p className="text-muted">Add a USD credit to {licensee.name}. The credit is recorded in the immutable administrative audit.</p><Form.Group className="mb-3"><Form.Label>Credit amount</Form.Label><Form.Control type="number" min="0.01" step="0.01" required value={amount} onChange={event => setAmount(event.target.value)} /></Form.Group><Form.Group><Form.Label>Reason</Form.Label><Form.Control as="textarea" rows={3} required maxLength={1000} value={reason} onChange={event => setReason(event.target.value)} /></Form.Group></Modal.Body>
    <Modal.Footer><Button variant="outline-secondary" onClick={onHide}>Cancel</Button><Button type="submit" disabled={saving}>{saving ? "Issuing…" : "Issue credit"}</Button></Modal.Footer>
  </Form></Modal>;
}
